import Grid from '@mui/material/Grid'
import React from 'react'
import { useSelector } from 'react-redux'

import { selectIsNewTaxesEnabled } from 'data/tenant/tenantSettingsSelectors'
import T from 'T'

import GeoZoneAutocomplete from './GeoZoneAutocomplete'
import { ZONE_FIELD_NAMES } from './geoZoneLocationExclusion'
import GeoZoneRoleField from './GeoZoneRoleField'
import { useGeoZoneMatches } from './useGeoZoneMatches'

import type { LatLng } from './useGeoZoneMatches'
import type { SxProps, Theme } from '@mui/material/styles'
import type { Control, FieldValues } from 'react-hook-form'

type GeoZoneLocationFieldsProps<TFieldValues extends FieldValues> = LatLng & {
  control: Control<TFieldValues>
  popperSx?: SxProps<Theme>
  // Two fields per row and the third full width, for a column too narrow for three chips fields across.
  twoPerRow?: boolean
}

// Tax is view-only: invoices already apply every zone containing the address, so there is nothing to save.
const GeoZoneLocationFields = <TFieldValues extends FieldValues>({
  control,
  latitude,
  longitude,
  popperSx,
  twoPerRow = false,
}: GeoZoneLocationFieldsProps<TFieldValues>) => {
  const isNewTaxesEnabled = useSelector(selectIsNewTaxesEnabled)
  const matches = useGeoZoneMatches({ latitude, longitude }, { skip: !isNewTaxesEnabled })
  const pairSize = twoPerRow ? { xs: 12, sm: 6 } : { xs: 12, sm: 4 }
  const lastSize = twoPerRow ? 12 : { xs: 12, sm: 4 }

  if (!isNewTaxesEnabled) return null

  return (
    <Grid container size={12} spacing={1.5}>
      <Grid size={pairSize}>
        <GeoZoneRoleField
          control={control}
          label={T.PRICING_ZONE}
          loading={matches.isLoading}
          name={ZONE_FIELD_NAMES.pricing}
          popperSx={popperSx}
          resolved={matches.isResolved}
          zones={matches.pricing}
        />
      </Grid>
      <Grid size={pairSize}>
        <GeoZoneAutocomplete
          viewOnly
          label={T.TAX_ZONE}
          loading={matches.isLoading}
          options={matches.tax}
          popperSx={popperSx}
          value={matches.tax}
        />
      </Grid>
      <Grid size={lastSize}>
        <GeoZoneRoleField
          control={control}
          label={T.SERVICE_ZONE}
          loading={matches.isLoading}
          name={ZONE_FIELD_NAMES.service}
          popperSx={popperSx}
          resolved={matches.isResolved}
          zones={matches.service}
        />
      </Grid>
    </Grid>
  )
}

export default GeoZoneLocationFields
